<template>
    <div class="multi-product-notes-wizard">
        <div v-if="currentProduct" class="product-content">
            <div v-if="currentProduct.notes && currentProduct.notes.length > 0" class="notes-section">
                <div class="product-name mb-3">
                    <h5 class="text-primary">{{ currentProduct.product_snapshot.nombre }}</h5>
                    <div v-if="hasPreselectedNotes" class="text-success small mt-1">
                        <i class="fa fa-check-circle mr-1"></i>
                        Este producto tiene nota preseleccionada
                    </div>
                </div>

                <div v-for="noteGroup in currentProduct.notes" :key="noteGroup.cas_number" class="cas-group mb-4">
                    <h6 class="cas-title">
                        CAS: {{ noteGroup.cas_number }}
                    </h6>

                    <div class="notes-list">
                        <b-form-radio-group v-model="internalSelectedNotes[noteGroup.cas_number]"
                            :name="`notes-${noteGroup.cas_number}`" stacked
                            @change="onNoteSelectionChange(noteGroup.cas_number, $event)"
                            :key="`radio-group-${noteGroup.cas_number}-${internalSelectedNotes[noteGroup.cas_number]}`">
                            <b-form-radio v-for="note in noteGroup.notes" :key="note.id" :value="parseInt(note.id)"
                                class="note-option mb-2">
                                <div class="note-content">
                                    <div class="note-text">{{ note.note }}</div>
                                    <div class="note-threshold">Umbral: {{ note.umbral }}</div>
                                </div>
                            </b-form-radio>
                        </b-form-radio-group>
                    </div>
                </div>
            </div>

            <div class="wizard-navigation d-flex justify-content-between align-items-center">
                <b-button variant="outline-secondary" :disabled="currentProductIndex === 0"
                    @click="navigateProduct(-1)">
                    <i class="fa fa-arrow-left mr-1"></i>
                    Anterior
                </b-button>

                <div class="navigation-info text-center">
                    <small class="text-muted">
                        Producto {{ currentProductIndex + 1 }} de {{ productsWithNotes.length }}
                    </small>
                    <div v-if="currentProduct && currentProduct.notes && currentProduct.notes.length > 0"
                        class="selection-status mt-1">
                        <small :class="hasRequiredSelections ? 'text-success' : 'text-warning'">
                            <i :class="hasRequiredSelections ? 'fa fa-check-circle' : 'fa fa-exclamation-triangle'"></i>
                            {{ getSelectionStatusMessage() }}
                        </small>
                    </div>
                </div>

                <b-button v-if="currentProductIndex < productsWithNotes.length - 1" variant="primary"
                    :disabled="!hasRequiredSelections" @click="navigateProduct(1)">
                    Siguiente
                    <i class="fa fa-arrow-right ml-1"></i>
                </b-button>

                <b-button v-else variant="success" :disabled="!hasRequiredSelections" @click="completeWizard">
                    Finalizar
                    <i class="fa fa-check ml-1"></i>
                </b-button>
            </div>
        </div>

        <div v-else class="empty-state text-center py-5">
            <i class="fa fa-info-circle fa-3x text-muted mb-3"></i>
            <h5 class="text-muted">No hay productos con notas para mostrar</h5>
        </div>
    </div>
</template>

<script>
export default {
    name: "MultiProductNotesWizard",
    props: {
        productsWithNotes: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            currentProductIndex: 0,
            internalSelectedNotes: {},
            productSelectedNotes: []
        };
    },
    computed: {
        currentProduct() {
            return this.productsWithNotes[this.currentProductIndex] || null;
        },

        progressPercentage() {
            if (this.productsWithNotes.length === 0) return 0;
            return Math.round(((this.currentProductIndex + 1) / this.productsWithNotes.length) * 100);
        },

        hasRequiredSelections() {
            if (!this.currentProduct || !this.currentProduct.notes || this.currentProduct.notes.length === 0) {
                return true;
            }
            return this.currentProduct.notes.every(noteGroup => {
                const casNumber = noteGroup.cas_number;
                const selected = this.internalSelectedNotes[casNumber];
                return typeof selected === 'number' && !isNaN(selected);
            });
        },

        isValid() {
            return this.hasRequiredSelections;
        },

        hasPreselectedNotes() {
            return this.currentProduct &&
                this.currentProduct.product_snapshot &&
                this.currentProduct.product_snapshot.selected_notes &&
                this.currentProduct.product_snapshot.selected_notes.length > 0;
        }
    },
    watch: {
        currentProductIndex: {
            handler(newIndex) {
                this.loadProductSelections(newIndex);
            },
            immediate: true
        },

        internalSelectedNotes: {
            handler(newSelections) {
                this.$forceUpdate();
            },
            deep: true
        }
    },
    methods: {
        getSelectionStatusMessage() {
            return this.hasRequiredSelections ? 'Nota seleccionada' : 'Seleccione la nota';
        },

        navigateProduct(step) {
            this.saveCurrentProductSelections();
            this.currentProductIndex += step;
        },

        onNoteSelectionChange(casNumber, selectedValue) {
            const parsed = parseInt(selectedValue);
            this.$set(this.internalSelectedNotes, casNumber, Number.isNaN(parsed) ? null : parsed);
            this.$forceUpdate();
        },

        completeWizard() {
            if (this.hasRequiredSelections) {
                this.saveCurrentProductSelections();
                const allSelections = this.processAllSelections();
                this.$emit('completed', allSelections);
            }
        },

        loadProductSelections(productIndex) {
            if (this.productSelectedNotes[productIndex]) {
                this.internalSelectedNotes = { ...this.productSelectedNotes[productIndex] };
                return;
            }
            const snapshotNotes = (
                this.currentProduct &&
                this.currentProduct.product_snapshot &&
                this.currentProduct.product_snapshot.selected_notes
            ) || [];

            const notes = (this.currentProduct && this.currentProduct.notes) || [];

            const selections = snapshotNotes.reduce((acc, { cas_number, note_id }) => {
                const parsed = parseInt(note_id);
                acc[cas_number] = Number.isNaN(parsed) ? null : parsed;
                return acc;
            }, {});

            notes.forEach(({ cas_number }) => {
                if (!(cas_number in selections)) {
                    selections[cas_number] = null;
                }
            });

            this.internalSelectedNotes = selections;
        },

        saveCurrentProductSelections() {
            this.$set(this.productSelectedNotes, this.currentProductIndex, { ...this.internalSelectedNotes });
        },

        processAllSelections() {
            return this.productsWithNotes.map((product, index) => {
                const productSelections = this.productSelectedNotes[index];
                if (!productSelections || !product.notes) return null;

                const selectedNotes = Object.entries(productSelections)
                    .filter(([, noteId]) => noteId !== null && noteId !== undefined)
                    .map(([cas, noteId]) => {
                        const selectedNote = product.notes
                            .find(group => group.cas_number === cas)
                            .notes.find(note => note.id === noteId);

                        return {
                            cas_number: cas,
                            note_id: noteId,
                            umbral: selectedNote.umbral
                        };
                    });

                return {
                    ppam_evaluated_product_id: product.id,
                    selected_notes: selectedNotes
                };
            }).filter(Boolean);
        }
    }
};
</script>

<style scoped>
.multi-product-notes-wizard {
    min-height: 400px;
}

.wizard-header {
    border-bottom: 1px solid #e9ecef;
    padding-bottom: 1rem;
}

.product-content {
    padding: 1rem 0;
}

.product-info {
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    padding: 1rem;
}

.product-icon {
    min-width: 50px;
}

.product-name-title {
    color: #495057;
    font-weight: 600;
}

.product-manufacturer {
    margin-bottom: 0.5rem;
}

.product-name {
    text-align: center;
    padding: 0.75rem;
    background: #f8f9fa;
    border-radius: 6px;
    border: 1px solid #dee2e6;
}

.product-name h5 {
    margin: 0;
    font-weight: 600;
}

.cas-group {
    border: 1px solid #e9ecef;
    border-radius: 8px;
    padding: 1rem;
    background: #f8f9fa;
}

.cas-title {
    color: #495057;
    font-weight: 600;
    margin-bottom: 0.75rem;
    border-bottom: 1px solid #dee2e6;
    padding-bottom: 0.5rem;
}

.notes-list {
    padding-left: 0.5rem;
}

.note-option {
    border: 1px solid #dee2e6;
    border-radius: 6px;
    margin-bottom: 0.5rem;
    transition: all 0.2s;
    background: white;
    cursor: pointer;
    position: relative;
}

.note-option:hover {
    background-color: #e9ecef;
    border-color: #007bff;
}

.note-option>>>.custom-control {
    margin-bottom: 0;
    min-height: auto;
    display: block;
    position: relative;
    padding: 0.75rem;
}

.note-option>>>.custom-control-input {
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    z-index: 2;
    width: 1rem;
    height: 1rem;
}

.note-option>>>.custom-control-label {
    cursor: pointer;
    position: relative;
    padding: 0;
    margin-bottom: 0;
    display: block;
    width: 100%;
}

.note-option>>>.custom-control-label::before,
.note-option>>>.custom-control-label::after {
    display: none !important;
}

.note-option>>>.custom-control-input:checked~.custom-control-label {
    background-color: #e9ecef;
    border-color: #007bff;
}

.note-option:has(input:checked) {
    background-color: #e9ecef;
    border-color: #007bff;
}

.note-content {
    width: 100%;
}

.note-text {
    font-size: 0.9rem;
    color: #495057;
    line-height: 1.4;
    font-weight: 500;
    margin-bottom: 0.25rem;
}

.note-threshold {
    font-size: 0.8rem;
    color: #6c757d;
    font-weight: 400;
}

.notes-section {
    padding-right: 0.5rem;
}

.wizard-navigation {
    margin-top: 2rem;
    padding-top: 1rem;
    border-top: 1px solid #e9ecef;
}

.navigation-info {
    flex-grow: 1;
}

.selection-status {
    font-size: 0.75rem;
}

.empty-state {
    color: #6c757d;
}

@media (max-width: 768px) {
    .wizard-navigation {
        flex-direction: row;
        gap: 0.5rem;
        justify-content: center;
    }

    .navigation-info {
        order: 0;
    }

    .product-info {
        padding: 0.75rem;
    }

    .product-info .d-flex {
        flex-direction: column;
        text-align: center;
    }

    .product-icon {
        margin-bottom: 1rem;
        margin-right: 0;
    }
}
</style>
